import Clarity from '@microsoft/clarity';
import * as Sentry from '@sentry/react';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { getMantleMarkFaviconHref } from '../assets/favicon';
import { IS_DEV, IS_PROD, IS_STAGING, ORN_ENV } from '../env';
import { getOptionalEnv } from '../lib/getEnv';
import { initMixpanel } from '../lib/mixpanel';
import MainApp from '../MainApp';
import ApolloProvider from '../providers/ApolloProvider';

import '../styles/app.css';

// Initialize Sentry before React renders
// The @sentry/react package automatically includes React integration
// Disable Sentry in dev environment
if (!IS_DEV) {
  const release = getOptionalEnv('VITE_SENTRY_RELEASE');
  Sentry.init({
    dsn: 'https://67d969c1f3c627ccd27565bb1cf90fd8@o4510502115606528.ingest.us.sentry.io/4510502117441536',
    environment: ORN_ENV,
    release,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // Enable tracing for performance monitoring
    tracesSampleRate: 1.0,
    // Integrate with browser tracing and replay
    // Note: React integration is automatically included with @sentry/react
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Only enable replay in production
    replaysSessionSampleRate: IS_PROD ? 1.0 : 0,
    replaysOnErrorSampleRate: IS_PROD ? 1.0 : 0,
  });
}

const clarityProjectId = getOptionalEnv('VITE_CLARITY_PROJECT_ID');
if (clarityProjectId) {
  Clarity.init(clarityProjectId);
}

initMixpanel();

const bootstrap = () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  const faviconHref = getMantleMarkFaviconHref();
  const favicon = <link rel="icon" type="image/svg+xml" href={faviconHref} />;

  const root = createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <Helmet>
          <title>Mantle{IS_DEV ? ' Dev' : IS_STAGING ? ' Staging' : ''}</title>
          {favicon}
        </Helmet>
        <ApolloProvider>
          <MainApp />
        </ApolloProvider>
      </HelmetProvider>
    </React.StrictMode>
  );
};

bootstrap();
